import React, { useState } from 'react';
import KnotWatermark from '../illustrations/KnotWatermark';

const faqs = [
  {
    id: 1,
    type: 'large',
    q: '¿Cómo funcionan las sesiones online?',
    a: 'Las sesiones se realizan por videollamada en un entorno privado y seguro. Solo necesitas un lugar tranquilo, conexión a internet y un dispositivo con cámara y audio. Te enviaremos el enlace y las indicaciones antes de la sesión.',
    expandable: true,
  },
  {
    id: 2,
    type: 'small',
    q: '¿Cuánto dura una sesión?',
    a: '50-60 minutos',
    expandable: false,
  },
  {
    id: 3,
    type: 'medium',
    q: '¿Cada cuánto son las sesiones?',
    a: 'Depende de tu momento y tus necesidades. Lo más frecuente es empezar con una sesión semanal, y más adelante pasar a quincenal o espaciar cuando haya más estabilidad.',
    expandable: true,
  },
  {
    id: 4,
    type: 'medium',
    q: '¿Qué trabajáis en terapia?',
    a: 'Acompañamos principalmente: ansiedad y crisis de angustia, estrés, rumiación y bloqueo emocional, crisis vitales (duelo, rupturas, cambios), trauma y experiencias difíciles, autoestima, límites y relaciones, procesos de identidad, y acompañamiento afirmativo LGTBIQA+.',
    expandable: true,
  },
  {
    id: 5,
    type: 'small',
    q: '¿Perspectiva de género?',
    a: 'Sí. Entendemos que el malestar emocional está influido por el contexto social, los roles de género, la desigualdad, la violencia y la presión cultural. Incorporamos esta mirada de forma respetuosa y sin imponer discursos, adaptándonos a tu realidad.',
    icon: 'check',
    expandable: true,
  },
  {
    id: 6,
    type: 'small',
    q: '¿Online funciona?',
    a: 'Sí, la evidencia científica respalda la eficacia de la terapia online para muchos motivos de consulta. Además, permite más flexibilidad, accesibilidad y continuidad.',
    icon: 'check',
    expandable: true,
  },
  {
    id: 7,
    type: 'small',
    q: '¿Y si me pongo nerviosa/o?',
    a: 'Es totalmente normal. Puedes venir con nervios, con vergüenza o sin saber por dónde empezar. Nos adaptamos a tu ritmo. La terapia no es un examen: es un espacio para estar como estás.',
    icon: 'heart',
    expandable: true,
  },
  {
    id: 8,
    type: 'small',
    q: '¿Es confidencial?',
    a: 'Sí. Todo lo que compartas en sesión es confidencial, siguiendo el código ético profesional y la normativa vigente. Solo se rompería la confidencialidad en situaciones excepcionales previstas por la ley (por ejemplo, riesgo grave para ti o para otras personas).',
    icon: 'lock',
    expandable: true,
  },
  {
    id: 9,
    type: 'small',
    q: '¿Cómo reservo?',
    a: 'Puedes reservar escribiéndonos a través del formulario de la web, email o WhatsApp. Te responderemos lo antes posible para ofrecerte disponibilidad.',
    icon: 'calendar',
    expandable: true,
  },
];

const icons = {
  check: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  heart: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  ),
  lock: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
  calendar: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
};

function ChevronIcon({ isOpen }) {
  return (
    <svg
      className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function LargeCard({ q, a, expandable, isOpen, onClick }) {
  return (
    <div
      onClick={expandable ? onClick : undefined}
      className={`col-span-2 p-6 bg-[var(--color-inerva-mint)] rounded-2xl transition-all duration-300 border border-transparent
        ${expandable ? 'cursor-pointer hover:shadow-lg hover:-translate-y-1' : ''}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-xs text-[var(--color-inerva-forest)]/60 mb-2 uppercase tracking-wide font-medium">
            {q}
          </p>
          <p className="text-lg font-medium text-[var(--color-inerva-forest)]">
            {!isOpen ? a.split('.')[0] + '...' : a.split('.')[0] + '.'}
          </p>
        </div>
        {expandable && (
          <span className="flex-shrink-0 mt-1 text-[var(--color-inerva-forest)]/40">
            <ChevronIcon isOpen={isOpen} />
          </span>
        )}
      </div>
      <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="text-[var(--color-inerva-text-light)] text-sm leading-relaxed">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}

function SmallCardStatic({ q, a }) {
  return (
    <div className="p-5 bg-white rounded-2xl shadow-sm border border-transparent hover:shadow-md hover:-translate-y-1 transition-all duration-300">
      <p className="text-xs text-[var(--color-inerva-forest)]/60 mb-1 uppercase tracking-wide font-medium">
        {q}
      </p>
      <p className="text-2xl font-semibold text-[var(--color-inerva-forest)]">
        {a}
      </p>
    </div>
  );
}

function MediumCard({ q, a, isOpen, onClick, colSpan = 1 }) {
  return (
    <div
      onClick={onClick}
      className={`${colSpan === 2 ? 'col-span-2' : ''} p-5 bg-white rounded-2xl shadow-sm border border-transparent
        hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-xs text-[var(--color-inerva-forest)]/60 mb-2 uppercase tracking-wide font-medium">
            {q}
          </p>
          <p className="font-medium text-[var(--color-inerva-forest)]">
            {!isOpen ? a.split('.')[0] + '.' : a.split('.')[0] + '.'}
          </p>
        </div>
        <span className="flex-shrink-0 mt-1 text-[var(--color-inerva-forest)]/40">
          <ChevronIcon isOpen={isOpen} />
        </span>
      </div>
      <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="text-sm text-[var(--color-inerva-text-light)] leading-relaxed">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}

function SmallCardExpandable({ q, a, icon, isOpen, onClick }) {
  return (
    <div
      onClick={onClick}
      className="p-4 bg-white rounded-2xl shadow-sm border border-transparent
        hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
    >
      <div className="flex items-start gap-2">
        {icon && (
          <span className="text-[var(--color-inerva-forest)]/50 mt-0.5">
            {icons[icon]}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[var(--color-inerva-forest)]/60 mb-1 uppercase tracking-wide font-medium">
            {q}
          </p>
          {!isOpen && (
            <p className="font-medium text-[var(--color-inerva-forest)] text-sm line-clamp-1">
              {a.split('.')[0]}
            </p>
          )}
        </div>
        <span className="flex-shrink-0 text-[var(--color-inerva-forest)]/40">
          <ChevronIcon isOpen={isOpen} />
        </span>
      </div>
      <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="text-sm text-[var(--color-inerva-text-light)] leading-relaxed">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}

function CTACard() {
  return (
    <a
      href="#contacto"
      className="p-5 bg-[var(--color-inerva-forest)] rounded-2xl text-white hover:opacity-90 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex flex-col justify-center items-center text-center"
    >
      <p className="text-sm opacity-80 mb-1">¿Más preguntas?</p>
      <p className="font-semibold">Escríbenos</p>
    </a>
  );
}

export default function FAQ() {
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section id="faq" className="bg-[var(--color-inerva-cream)] py-20 relative overflow-hidden">
      <KnotWatermark position="center" />
      <div className="container-narrow relative">
        <div className="text-center mb-12">
          <h2 className="text-[var(--color-inerva-forest)]">Preguntas frecuentes</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto items-start">
          <LargeCard
            q={faqs[0].q}
            a={faqs[0].a}
            expandable={faqs[0].expandable}
            isOpen={openId === faqs[0].id}
            onClick={() => toggle(faqs[0].id)}
          />
          <SmallCardStatic q={faqs[1].q} a={faqs[1].a} />

          <MediumCard
            q={faqs[2].q}
            a={faqs[2].a}
            isOpen={openId === faqs[2].id}
            onClick={() => toggle(faqs[2].id)}
          />
          <MediumCard
            q={faqs[3].q}
            a={faqs[3].a}
            isOpen={openId === faqs[3].id}
            onClick={() => toggle(faqs[3].id)}
            colSpan={2}
          />

          <SmallCardExpandable
            q={faqs[4].q}
            a={faqs[4].a}
            icon={faqs[4].icon}
            isOpen={openId === faqs[4].id}
            onClick={() => toggle(faqs[4].id)}
          />
          <SmallCardExpandable
            q={faqs[5].q}
            a={faqs[5].a}
            icon={faqs[5].icon}
            isOpen={openId === faqs[5].id}
            onClick={() => toggle(faqs[5].id)}
          />
          <SmallCardExpandable
            q={faqs[6].q}
            a={faqs[6].a}
            icon={faqs[6].icon}
            isOpen={openId === faqs[6].id}
            onClick={() => toggle(faqs[6].id)}
          />

          <SmallCardExpandable
            q={faqs[7].q}
            a={faqs[7].a}
            icon={faqs[7].icon}
            isOpen={openId === faqs[7].id}
            onClick={() => toggle(faqs[7].id)}
          />
          <SmallCardExpandable
            q={faqs[8].q}
            a={faqs[8].a}
            icon={faqs[8].icon}
            isOpen={openId === faqs[8].id}
            onClick={() => toggle(faqs[8].id)}
          />
          <CTACard />
        </div>
      </div>
    </section>
  );
}
